import React, { useEffect, useState, useMemo, useRef } from "react";
import { FiEdit, FiTrash2, FiEye } from "react-icons/fi";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import EditViewJobPostModal from "./EditViewJobPostModal";

export default function JobListingTable({ refreshTrigger }) {
    const [jobPosts, setJobPosts] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    useEffect(() => {
        fetchJobPosts();
    }, [refreshTrigger]);

    const fetchJobPosts = async () => {
        setLoading(true);
        try {
            const res = await fetch( "https://ims.holdthechild.net/api/job-posts");
            const json = await res.json();
            if (Array.isArray(json.data)) {
                // Sort jobs by creation date (newest first)
                const sortedJobs = [...json.data].sort((a, b) => {
                    // Try to use createdAt field first
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(parseInt(a._id.substring(0, 8), 16) * 1000);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(parseInt(b._id.substring(0, 8), 16) * 1000);
                    return dateB - dateA; // Descending order (newest first)
                });
                setJobPosts(sortedJobs);
            } else {
                console.warn("Unexpected jobPosts structure:", json);
                setJobPosts([]);
            }
        } catch (error) {
            console.error("Failed to fetch job posts:", error);
            setJobPosts([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredJobs = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return jobPosts;
        return jobPosts.filter((job) =>
            (job.vacancy && job.vacancy.toLowerCase().includes(q)) ||
            (job.department && job.department.toLowerCase().includes(q)) ||
            (job.location && job.location.toLowerCase().includes(q))
        );
    }, [jobPosts, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const paginatedJobs = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredJobs.slice(start, start + itemsPerPage);
    }, [filteredJobs, currentPage]);

    const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

    const getStatusStyle = (status) => {
        switch (status) {
            case "Open": return "text-green-600 bg-green-100 px-2 py-1 rounded-full text-sm";
            case "In progress": return "text-orange-600 bg-orange-100 px-2 py-1 rounded-full text-sm";
            case "Closed": return "text-red-600 bg-red-100 px-2 py-1 rounded-full text-sm";
            default: return "text-gray-600";
        }
    };

    const handleEditClick = (job) => {
        setSelectedJob(job);
        setIsEditModalOpen(true);
    };

    const handleViewClick = (job) => {
        setSelectedJob(job);
        setIsViewModalOpen(true);
    };

    const handleDelete = async (jobId) => {
        if (!window.confirm("Are you sure you want to delete this job post?")) {
            return;
        }
        try {
            const response = await fetch(
                `"https://ims.holdthechild.net"/api/job-posts/${jobId}`,
                {
                    method: "DELETE",
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete job post.");
            }

            setJobPosts((prevJobs) => prevJobs.filter((job) => job._id !== jobId));
            toast.success("Job post deleted successfully!");
        } catch (error) {
            toast.error(error.message);
        }
    };

    // Add this missing function
    const handleJobUpdate = (updatedJob) => {
        setJobPosts((prevJobs) =>
            prevJobs.map((job) => (job._id === updatedJob._id ? updatedJob : job))
        );
    };

    const mapJobToExportRow = (job) => ({
        "Job Title": job.vacancy || "",
        Department: job.department || "",
        Location: job.location || "",
        "Listing Type": job.typeOfListing || "",
        Deadline: job.deadline ? moment(job.deadline).format("YYYY-MM-DD") : "-",
        Status: "Open",
    });

    const exportToExcel = () => {
        if (!filteredJobs.length) {
            alert("No job posts to export!");
            return;
        }
        const exportData = filteredJobs.map(mapJobToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Job Listings");
        const maxColWidths = Object.keys(exportData[0]).map((col) => {
            const maxLength = Math.max(
                col.length,
                ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))
            );
            return { wch: maxLength + 5 };
        });
        worksheet["!cols"] = maxColWidths;
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(dataBlob, "job_listings_export.xlsx");
    };

    const exportToCSV = () => {
        if (!filteredJobs.length) {
            alert("No job posts to export!");
            return;
        }
        const exportData = filteredJobs.map(mapJobToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(csvBlob, "job_listings_export.csv");
    };

    const exportToPDF = () => {
        if (!filteredJobs.length) {
            alert("No job posts to export!");
            return;
        }
        const exportData = filteredJobs.map(mapJobToExportRow);
        const doc = new jsPDF("l", "pt", "a4");
        const columns = Object.keys(exportData[0]);
        doc.setFontSize(18);
        doc.setTextColor(237, 125, 49);
        doc.text("Job Listings Export", 40, 40);
        autoTable(doc, {
            startY: 60,
            head: [columns],
            body: exportData.map((row) => columns.map((col) => row[col])),
            styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [237, 125, 49] },
            theme: "striped",
            margin: { left: 20, right: 20 },
            didDrawPage: (data) => {
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(10);
                doc.setTextColor(150);
                doc.text(
                    `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
                    data.settings.margin.left,
                    doc.internal.pageSize.height - 10
                );
            },
        });
        doc.save("job_listings_export.pdf");
    };

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        switch (format) {
            case "excel":
                exportToExcel();
                break;
            case "csv":
                exportToCSV();
                break;
            case "pdf":
                exportToPDF();
                break;
            default:
                alert("Unsupported export format");
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="mt-8 bg-white rounded shadow p-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Job Listing Table</h2>

            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <input
                    type="search"
                    placeholder="Search job posts by title, department, or location"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    aria-label="Search job posts"
                />

                <div className="relative inline-block text-left" ref={dropdownRef}>
                    <button
                        onClick={() => setExportDropdownOpen((open) => !open)}
                        type="button"
                        className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        aria-haspopup="true"
                        aria-expanded={exportDropdownOpen}
                        aria-controls="export-menu"
                    >
                        Export
                        <svg
                            className="ml-2 -mr-1 h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>

                    {exportDropdownOpen && (
                        <div
                            id="export-menu"
                            className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="export-menu-button"
                            tabIndex={-1}
                        >
                            <div className="py-1">
                                {["excel", "csv", "pdf"].map((format) => (
                                    <button
                                        key={format}
                                        onClick={() => handleExport(format)}
                                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                                        role="menuitem"
                                    >
                                        Export as {format.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <p className="text-center py-4">Loading job posts...</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="bg-orange-100 text-gray-600 text-left">
                                <th className="px-4 text-orange-600 py-2">Title</th>
                                <th className="px-4 text-orange-600 py-2">Department</th>
                                <th className="px-4 text-orange-600 py-2">Location</th>
                                <th className="px-4 text-orange-600 py-2">Type</th>
                                <th className="px-4 text-orange-600 py-2">Deadline</th>
                                <th className="px-4 text-orange-600 py-2">Status</th>
                                <th className="px-4 text-orange-600 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedJobs.length > 0 ? (
                                paginatedJobs.map((job, i) => (
                                    <tr key={job._id || i} className="border-b">
                                        <td className="py-2 px-4">{job.vacancy}</td>
                                        <td className="py-2 px-4">{job.department}</td>
                                        <td className="py-2 px-4">{job.location}</td>
                                        <td className="py-2 px-4">{job.typeOfListing}</td>
                                        <td className="py-2 px-4">
                                            {job.deadline ? new Date(job.deadline).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="py-2 px-4">
                                            <span className={getStatusStyle("Open")}>Open</span>
                                        </td>
                                        <td className="py-2 px-4 flex gap-2">
                                            <button
                                                onClick={() => handleViewClick(job)}
                                                className="text-gray-600 hover:text-blue-500"
                                                title="View Details"
                                            >
                                                <FiEye />
                                            </button>
                                            <button
                                                onClick={() => handleEditClick(job)}
                                                className="text-orange-600 hover:text-orange-800"
                                                title="Edit"
                                            >
                                                <FiEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(job._id)}
                                                className="text-red-500 hover:text-red-700"
                                                title="Delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-4 text-gray-500">
                                        No job posts found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`rounded px-3 py-1 ${currentPage === 1
                            ? "cursor-not-allowed bg-gray-300"
                            : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                            }`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`rounded px-3 py-1 ${currentPage === i + 1
                                ? "bg-orange-300 text-orange-900 font-semibold"
                                : "bg-gray-200 hover:bg-gray-300"
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`rounded px-3 py-1 ${currentPage === totalPages
                            ? "cursor-not-allowed bg-gray-300"
                            : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}

            {isEditModalOpen && (
                <EditViewJobPostModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    jobPost={selectedJob}
                    onUpdate={handleJobUpdate}
                    isEditing={true}
                />
            )}

            {isViewModalOpen && (
                <EditViewJobPostModal
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    jobPost={selectedJob}
                    onUpdate={handleJobUpdate}
                    isEditing={false}
                />
            )}
        </div>
    );
}